import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { CreditCardInvoice } from "@meupedaco/shared";
import { useAuth } from "../../auth/AuthContext";
import { PageState } from "../../components/PageState";
import { usePagedQuery } from "../../hooks/usePagedQuery";
import { formatDateBR, formatMoneyBR } from "../../utils/format";

type CardOption = { id: number; label: string };

type InvoiceRow = CreditCardInvoice & {
  card?: { name: string; brand?: string | null; last4?: string | null };
};

const STATUS_PT: Record<string, { label: string; cls: string }> = {
  OPEN:   { label: "Aberta",   cls: "text-bg-warning" },
  CLOSED: { label: "Fechada",  cls: "text-bg-secondary" },
  PAID:   { label: "Paga",     cls: "text-bg-success" },
};

export const InvoicesPage: React.FC = () => {
  const { client } = useAuth();
  const [cards, setCards] = useState<CardOption[]>([]);
  const [cardId, setCardId] = useState<number | "">("");
  const [status, setStatus] = useState("");
  const [competence, setCompetence] = useState("");

  useEffect(() => {
    client.get("/modules/cards?take=100&skip=0").then(async (res) => {
      if (!res.ok) return;
      const payload = (await res.json()) as {
        data: Array<{ id: number; name: string; last4?: string | null }>;
      };
      setCards(
        payload.data.map((c) => ({
          id: c.id,
          label: c.last4 ? `${c.name} ···· ${c.last4}` : c.name,
        }))
      );
    });
  }, [client]);

  const {
    rows,
    total,
    loading,
    error,
    page,
    take,
    setPage,
    q,
    setQ,
  } = usePagedQuery<InvoiceRow>({
    endpoint: "/modules/cards/invoices",
    filters: useMemo(() => {
      const [year, month] = competence.split("-").map(Number);
      return {
        cardId: cardId || undefined,
        status: status || undefined,
        competenceYear: year || undefined,
        competenceMonth: month || undefined,
      };
    }, [cardId, competence, status]),
  });

  const paginationText = useMemo(() => {
    if (total === 0) return "0 resultados";
    const start = (page - 1) * take + 1;
    const end = Math.min(page * take, total);
    return `${start} - ${end} de ${total}`;
  }, [page, take, total]);

  return (
    <div className="container py-4">
      <h1 className="mb-3">Faturas</h1>
      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-5">
              <label className="form-label">Cartão</label>
              <select
                className="form-select"
                value={cardId}
                onChange={(e) => setCardId(e.target.value === "" ? "" : Number(e.target.value))}
              >
                <option value="">Todos os cartões</option>
                {cards.map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Competencia</label>
              <input
                className="form-control"
                type="month"
                value={competence}
                onChange={(e) => setCompetence(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="">Todos</option>
                <option value="OPEN">Aberta</option>
                <option value="CLOSED">Fechada</option>
                <option value="PAID">Paga</option>
              </select>
            </div>
            <div className="col-md-2">
              <input
                className="form-control"
                placeholder="Buscar descrição"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <PageState loading={loading} error={error}>
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Cartão</th>
                    <th>Competencia</th>
                    <th>Vencimento</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((item) => {
                    const statusInfo = STATUS_PT[item.status] ?? { label: item.status, cls: "text-bg-light" };
                    return (
                      <tr key={item.id}>
                        <td>
                          <span className="fw-semibold">{item.card?.name ?? "—"}</span>
                          {item.card?.last4 && (
                            <span className="text-muted ms-1 small">···· {item.card.last4}</span>
                          )}
                        </td>
                        <td>
                          {item.competenceYear}-{String(item.competenceMonth).padStart(2, "0")}
                        </td>
                        <td>{formatDateBR(item.dueDate)}</td>
                        <td>{formatMoneyBR(item.totalAmount)}</td>
                        <td>
                          <span className={`badge ${statusInfo.cls}`}>{statusInfo.label}</span>
                        </td>
                        <td className="text-end">
                          <Link
                            className="btn btn-sm btn-outline-primary"
                            to={`/financeiro/faturas/${item.id}`}
                          >
                            Ver
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center text-muted">
                        Nenhum registro.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </PageState>
          <div className="d-flex justify-content-between align-items-center mt-2">
            <small className="text-muted">{paginationText}</small>
            <div className="btn-group">
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={page <= 1}
                onClick={() => setPage(Math.max(1, page - 1))}
              >
                Anterior
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={page * take >= total}
                onClick={() => setPage(page + 1)}
              >
                Proximo
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
